import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import styled from "styled-components";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useMediaQuery } from "@mui/material";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LinkedIn from "@mui/icons-material/LinkedIn";
import { useStoreState } from "app/state/store/hooks";
import { colors, customBreakpointsMax } from "app/theme";
import { useEmailSignup } from "app/hooks/useEmailSignup";
import { CtaCommonBtn } from "app/components/cta-common-btn";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { getUrlParamsFromSelectedFilters } from "app/hooks/useUrlFilters";
import { ReactComponent as HeaderToolbarLogo } from "app/assets/vectors/HeaderToolbarLogo.svg";
import { ReactComponent as FooterLinkAIDAExplorer } from "app/assets/vectors/FooterLinkAIDAExplorer.svg";
import { ReactComponent as FooterLinkDiscoverSectors } from "app/assets/vectors/FooterLinkDiscoverSectors.svg";
import { ReactComponent as FooterLinkAIDAExplorerHover } from "app/assets/vectors/FooterLinkAIDAExplorerHover.svg";
import { ReactComponent as FooterLinkDiscoverLocations } from "app/assets/vectors/FooterLinkDiscoverLocations.svg";
import { ReactComponent as FooterLinkDiscoverSectorsHover } from "app/assets/vectors/FooterLinkDiscoverSectorsHover.svg";
import { ReactComponent as FooterLinkDiscoverOrganisations } from "app/assets/vectors/FooterLinkDiscoverOrganisations.svg";
import { ReactComponent as FooterLinkSearchFilterActivities } from "app/assets/vectors/FooterLinkSearchFilterActivities.svg";
import { ReactComponent as FooterLinkDiscoverLocationsHover } from "app/assets/vectors/FooterLinkDiscoverLocationsHover.svg";
import { ReactComponent as FooterLinkDiscoverOrganisationsHover } from "app/assets/vectors/FooterLinkDiscoverOrganisationsHover.svg";
import { ReactComponent as FooterLinkSearchFilterActivitiesHover } from "app/assets/vectors/FooterLinkSearchFilterActivitiesHover.svg";
import { useCMSData } from "app/hooks/useCMSData";
import { getCMSDataField } from "app/utils/getCMSDataField";
import { ArrowOutward } from "@mui/icons-material";
import { InfoTooltip } from "../info-tooltip";

const FooterEl = styled.footer`
  width: 100%;
  z-index: 1302;
  position: relative;
  background: #f7f7f7;
  border-top: 1px solid rgba(161, 174, 189, 0.2);
`;

const ResponsiveFooter: React.FC<{
  email: string;
  message: string;
  isPublisher: boolean;
  handleSubmit: () => void;
  setEmail: (email: string) => void;
  setMessage: (message: string) => void;
}> = (props) => {
  const cmsData = useCMSData({ returnData: true });
  const { email, handleSubmit, message, setEmail, setMessage, isPublisher } =
    props;

  return (
    <Grid
      container
      spacing={2}
      sx={{ hr: { borderBottom: "1px solid rgba(161, 174, 189, 0.2)" } }}
    >
      <Grid item xs={12}>
        <HeaderToolbarLogo />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            gap: "15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            fontSize="14px"
            fontWeight="400"
            component="label"
            id="newsletter-label"
          >
            {getCMSDataField(
              cmsData,
              "componentsFooter.newsLetterText",
              "Subscribe to our newsletter"
            )}
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: "15px", md: "30px" },
              flexDirection: { xs: "column", md: "row" },
              "> input": {
                flex: "1",
                height: "32px",
                fontSize: "14px",
                minHeight: "32px",
                padding: "0 20px",
                borderRadius: "5px",
                color: colors.text.title,
                background: colors.primary.white,
                width: { xs: "100%", md: "268px" },
                border: `1px solid ${colors.secondary.iconGray}`,
                "::placeholder": { color: colors.text.title },
                ":focus": { outline: "none" },
              },
            }}
          >
            <input
              type="text"
              value={email}
              placeholder={getCMSDataField(
                cmsData,
                "componentsFooter.newsLetterPlaceholderText",
                "Email"
              )}
              id="newsletter-input"
              data-cy="newsletter-input"
              aria-labelledby="newsletter-label"
              onChange={(e) => {
                setEmail(e.target.value);
                if (message) {
                  setMessage("");
                }
              }}
            />
            <Button
              sx={{
                width: "170px",
                height: "32px",
                fontSize: "14px",
                padding: "8px 10px",
                textTransform: "none",
                color: colors.primary.white,
                background: isPublisher
                  ? colors.primary.green
                  : colors.primary.blue,
                justifyContent: "space-between",
              }}
              variant="contained"
              onClick={handleSubmit}
              data-cy="newsletter-submit-btn"
            >
              {getCMSDataField(
                cmsData,
                "componentsFooter.newsLetterButtonText",
                "Subscribe"
              )}
            </Button>
            <Typography
              data-cy="newsletter-submit-message"
              fontSize="14px"
              fontWeight="700"
              marginTop="-10px"
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          sx={{
            a: {
              fontSize: "14px",
              textDecoration: "none",
              color: colors.text.title,
            },
          }}
        >
          <Grid item xs={6} md={4}>
            <Link to="/">
              {getCMSDataField(
                cmsData,
                "componentsFooter.homepageLinkText",
                "Homepage"
              )}
            </Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/data-services">
              {getCMSDataField(
                cmsData,
                "general.dataServicesText",
                "Data Services"
              )}
            </Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/pricing">Pricing</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <a
              target="_blank"
              rel="noreferrer noopen"
              href="https://support.aida.tools"
            >
              User Guide
            </a>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/contact">Contact</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/login">Sign In / Account</Link>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" fontSize="14px" marginBottom="15px">
          {getCMSDataField(
            cmsData,
            "general.aidaExplorerText",
            "AIDA Explorer"
          )}
        </Typography>
        <Grid
          container
          spacing={2}
          sx={{
            a: {
              fontSize: "14px",
              textDecoration: "none",
              color: colors.text.title,
            },
          }}
        >
          <Grid item xs={6} md={4}>
            <Link to="/explorer">Explorer Home</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/explorer/locations">Explore Locations</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/explorer/organisations">Explore Organisactions</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/explorer/sectors">Explore Sectors</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/explorer/activities">Explore Actvities and Search</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/explorer/favourites">Your Favourites</Link>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" fontSize="14px" marginBottom="15px">
          AIDA Publisher
        </Typography>
        <Grid
          container
          spacing={2}
          sx={{
            a: {
              fontSize: "14px",
              textDecoration: "none",
              color: colors.text.title,
            },
          }}
        >
          <Grid item xs={6} md={4}>
            <Link to="/publisher">Publisher Home</Link>
          </Grid>
          <Grid item xs={6} md={4}>
            <Link to="/data-hub">Your Data Hub</Link>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid
        item
        container
        spacing={2}
        xs={12}
        sx={{
          a: {
            fontSize: "14px",
            textDecoration: "none",
            color: colors.text.title,
            "&:hover": { textDecoration: "underline" },
          },
        }}
      >
        <Grid item xs={12} sm={5} md={4}>
          <Typography fontSize="14px" marginTop="5px" color={colors.text.title}>
            The Open Data Company B.V.
            <br />
            Keizersgracht 520H, 1017 EK
            <br />
            Amsterdam, The Netherlands
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={7}
          md={8}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="mailto:info@aida.tools"
          >
            {getCMSDataField(
              cmsData,
              "componentsFooter.email",
              "info@aida.tools"
            )}
          </a>
          <Button
            target="_blank"
            aria-label="LinkedIn"
            rel="noopener noreferrer"
            href="https://www.linkedin.com/company/the-open-data-company"
            sx={{
              fontWeight: 400,
            }}
          >
            LinkedIn
            <ArrowOutward fontSize="small" />
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const Footer: React.FC = () => {
  const cmsData = useCMSData({ returnData: true });

  const links = [
    {
      label: getCMSDataField(cmsData, "ctaFooter.explorer", "AIDA Explorer"),
      link: "/explorer",
      bgIcon: <FooterLinkAIDAExplorer />,
      bgIconHover: <FooterLinkAIDAExplorerHover />,
    },
    {
      label: getCMSDataField(
        cmsData,
        "ctaFooter.locations",
        "Discover Locations"
      ),
      link: "/explorer/locations",
      bgIcon: <FooterLinkDiscoverLocations />,
      bgIconHover: <FooterLinkDiscoverLocationsHover />,
    },
    {
      label: getCMSDataField(
        cmsData,
        "ctaFooter.organisations",
        "Discover Organisations"
      ),
      link: "/explorer/organisations",
      bgIcon: <FooterLinkDiscoverOrganisations />,
      bgIconHover: <FooterLinkDiscoverOrganisationsHover />,
    },
    {
      label: getCMSDataField(cmsData, "ctaFooter.sectors", "Discover Sectors"),
      link: "/explorer/sectors",
      bgIcon: <FooterLinkDiscoverSectors />,
      bgIconHover: <FooterLinkDiscoverSectorsHover />,
    },
    {
      label: getCMSDataField(
        cmsData,
        "ctaFooter.activities",
        "Search & Filter Activities"
      ),
      link: "/explorer/activities",
      bgIcon: <FooterLinkSearchFilterActivities />,
      bgIconHover: <FooterLinkSearchFilterActivitiesHover />,
    },
    {
      label: getCMSDataField(
        cmsData,
        "ctaFooter.webinar",
        "Join the monthly AIDA Webinar"
      ),
      tooltip: "Coming soon",
    },
  ];

  const location = useLocation();
  const urlParams = useSearchParams()[0];
  const mobile = useMediaQuery(customBreakpointsMax.mobile);
  const mobileOrTablet = useMediaQuery(customBreakpointsMax.tablet);

  const { email, handleSubmit, message, setEmail, setMessage } =
    useEmailSignup();

  const [showWebinarTooltip, setShowWebinarTooltip] = React.useState(false);
  const selectedFilters = useStoreState((state) => state.appliedFilters);

  const isPublisher = React.useMemo(() => {
    return location.pathname.indexOf("/publisher") > -1;
  }, [location.pathname]);

  const inActivityPage = React.useMemo(
    () => location.pathname.indexOf("/explorer/activities/") > -1,
    [location.pathname]
  );

  const locationSearch = React.useMemo(() => {
    if (location.search) {
      return location.search;
    }
    if (inActivityPage) {
      const queryString = decodeURIComponent(
        getUrlParamsFromSelectedFilters(selectedFilters, urlParams).toString()
      );
      return `${queryString.length > 0 ? "?" : ""}${queryString}`;
    }
    return "";
  }, [location.search, inActivityPage, selectedFilters, urlParams]);

  return (
    <FooterEl data-cy="footer">
      <Container
        maxWidth="lg"
        sx={{
          padding: {
            xs: "20px 24px",
            md: "20px 30px",
            lg: "20px 0 !important",
          },
        }}
      >
        {!mobileOrTablet && (
          <React.Fragment>
            <Box
              sx={{
                width: "100%",
                height: "64px",
                display: "flex",
                flexDirection: "row",
                marginBottom: "24px",
                justifyContent: "space-between",
              }}
            >
              {links.map((link, index) => (
                <CtaCommonBtn
                  {...link}
                  variant="footer"
                  key={link.label}
                  link={link.link ? link.link + locationSearch : undefined}
                  bgColor={
                    isPublisher && index === links.length - 1 ? "green" : "blue"
                  }
                />
              ))}
            </Box>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={12} md={4} lg={2}>
                <Box
                  sx={{
                    gap: "15px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    a: {
                      fontSize: "14px",
                      textDecoration: "none",
                      color: colors.text.title,
                      "&:hover": { textDecoration: "underline" },
                    },
                  }}
                >
                  <HeaderToolbarLogo />
                  <Typography
                    fontSize="14px"
                    marginTop="5px"
                    color={colors.text.title}
                  >
                    The Open Data Company B.V.
                    <br />
                    Keizersgracht 520H,
                    <br />
                    1017 EK Amsterdam,
                    <br />
                    The Netherlands
                  </Typography>
                  <Box>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="mailto:info@aida.tools"
                    >
                      {getCMSDataField(
                        cmsData,
                        "componentsFooter.email",
                        "info@aida.tools"
                      )}
                    </a>
                    <br />
                    <a href="tel:+31854011885">Tel: +31 85 401 1885</a>
                  </Box>
                  <IconButton
                    target="_blank"
                    sx={{ padding: 0 }}
                    aria-label="LinkedIn"
                    rel="noopener noreferrer"
                    href="https://www.linkedin.com/company/the-open-data-company"
                  >
                    <LinkedIn htmlColor={colors.text.title} />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={2}>
                <Box
                  sx={{
                    gap: "15px",
                    display: "flex",
                    paddingTop: "40px",
                    flexDirection: "column",
                    "> a": {
                      fontSize: "14px",
                      textDecoration: "none",
                      color: colors.text.title,
                    },
                  }}
                >
                  <Link to="/">
                    {getCMSDataField(
                      cmsData,
                      "componentsFooter.homepageLinkText",
                      "Homepage"
                    )}
                  </Link>
                  <Link to="/data-services">
                    {getCMSDataField(
                      cmsData,
                      "general.dataServicesText",
                      "Data Services"
                    )}
                  </Link>
                  <Link to="/pricing">Pricing</Link>
                  <a
                    target="_blank"
                    rel="noreferrer noopen"
                    href="https://support.aida.tools"
                  >
                    User Guide
                  </a>
                  <Link to="/contact">Contact</Link>
                  <Link to="/login">Sign In / Account</Link>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={2}>
                <Typography
                  variant="h6"
                  fontSize="14px"
                  paddingTop="40px"
                  marginBottom="15px"
                >
                  {getCMSDataField(
                    cmsData,
                    "general.aidaExplorerText",
                    "AIDA Explorer"
                  )}
                </Typography>
                <Box
                  sx={{
                    gap: "15px",
                    display: "flex",
                    flexDirection: "column",
                    "> a": {
                      fontSize: "14px",
                      textDecoration: "none",
                      color: colors.text.title,
                    },
                  }}
                >
                  <Link to="/explorer">Explorer Home</Link>
                  <Link to="/explorer/locations">Explore Locations</Link>
                  <Link to="/explorer/organisations">
                    Explore Organisactions
                  </Link>
                  <Link to="/explorer/sectors">Explore Sectors</Link>
                  <Link to="/explorer/activities">
                    Explore Actvities and Search
                  </Link>
                  <Link to="/explorer/favourites">Your Favourites</Link>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={2}>
                <Typography
                  variant="h6"
                  fontSize="14px"
                  paddingTop="40px"
                  marginBottom="15px"
                >
                  AIDA Publisher
                </Typography>
                <Box
                  sx={{
                    gap: "15px",
                    display: "flex",
                    flexDirection: "column",
                    "> a": {
                      fontSize: "14px",
                      textDecoration: "none",
                      color: colors.text.title,
                    },
                  }}
                >
                  <Link to="/publisher">Publisher Home</Link>
                  <Link to="/data-hub">Your Data Hub</Link>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Box
                  sx={{
                    gap: "15px",
                    display: "flex",
                    paddingTop: "40px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <InfoTooltip
                    open={showWebinarTooltip}
                    mode="light"
                    info={
                      <Box>
                        <Typography
                          variant="h6"
                          fontSize="14px"
                          lineHeight={"normal"}
                        >
                          Subscribe to our newsletter to get notified about
                          Webinar!
                        </Typography>
                        <Typography
                          variant="h6"
                          fontSize="14px"
                          fontWeight={400}
                          lineHeight={"normal"}
                        >
                          Our Webinar is to be communicated. In the session
                          functions of AIDA, and overview of what you can do on
                          the platform will be discussed.
                        </Typography>
                        <Typography
                          marginLeft={"auto"}
                          fontSize="10px"
                          fontWeight={700}
                          lineHeight={"normal"}
                          marginTop="2px"
                          variant="body2"
                          color={colors.text.title}
                          sx={{
                            textDecoration: "underline",
                            width: "max-content",
                            cursor: "pointer",
                          }}
                          onClick={() => setShowWebinarTooltip(false)}
                        >
                          Close Tooltip
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Typography
                      fontSize="14px"
                      fontWeight="400"
                      component="label"
                      id="newsletter-label"
                      aria-label="Subscribe to our newsletter"
                    >
                      {getCMSDataField(
                        cmsData,
                        "componentsFooter.newsLetterText",
                        "Subscribe to our newsletter"
                      )}
                    </Typography>
                  </InfoTooltip>
                  <Box
                    sx={{
                      gap: { xs: "15px", md: "30px", lg: "15px" },
                      display: "flex",
                      flexDirection: { xs: "column", md: "row", lg: "column" },
                      alignItems: "flex-start",
                      width: "100%",
                      "> input": {
                        width: { xs: "100%", md: "268px" },
                        flex: { md: "1", lg: "unset" },
                        height: "32px",
                        fontSize: "14px",
                        padding: "0 20px",
                        borderRadius: "5px",
                        color: colors.text.title,
                        background: colors.primary.white,
                        border: `1px solid ${colors.secondary.iconGray}`,
                        "::placeholder": {
                          color: colors.text.title,
                        },
                        ":focus": {
                          outline: "none",
                        },
                      },
                    }}
                  >
                    <input
                      type="text"
                      value={email}
                      placeholder={getCMSDataField(
                        cmsData,
                        "componentsFooter.newsLetterPlaceholderText",
                        "Email"
                      )}
                      id="newsletter-input"
                      data-cy="newsletter-input"
                      aria-labelledby="newsletter-label"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (message) {
                          setMessage("");
                        }
                      }}
                    />
                    <Button
                      sx={{
                        height: "32px",
                        fontSize: "14px",
                        padding: "8px 10px",
                        textTransform: "none",
                        color: colors.primary.white,
                        background: isPublisher
                          ? colors.primary.green
                          : colors.primary.blue,
                        width: {
                          md: "254px",
                          lg: "max-content",
                        },
                        justifyContent: "space-between",
                      }}
                      variant="contained"
                      onClick={handleSubmit}
                      data-cy="newsletter-submit-btn"
                    >
                      {getCMSDataField(
                        cmsData,
                        "componentsFooter.newsLetterButtonText",
                        "Subscribe"
                      )}
                    </Button>
                    <Typography
                      data-cy="newsletter-submit-message"
                      fontSize="14px"
                      fontWeight="700"
                      marginTop="-10px"
                    >
                      {message}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </React.Fragment>
        )}
        {mobileOrTablet && (
          <ResponsiveFooter
            email={email}
            message={message}
            setEmail={setEmail}
            setMessage={setMessage}
            isPublisher={isPublisher}
            handleSubmit={handleSubmit}
          />
        )}
      </Container>
      <Box
        sx={{
          gap: "20px",
          rowGap: {
            xs: "10px",
            md: "20px",
          },
          width: "100%",
          height: { md: "32px" },
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: { xs: "space-between", md: "center" },
          color: colors.text.title,
          background: colors.secondary.disableText,
          padding: {
            xs: "8px 20px",
            md: "0",
          },
          "> a": {
            fontSize: { xs: "14px" },
            textDecoration: "none",
            color: colors.text.title,
          },
          "> p": {
            fontSize: { md: "14px" },
            color: colors.text.title,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap="20px">
          <Typography fontSize="18px !important" color={colors.text.title}>
            ©
          </Typography>
          <Typography fontSize="14px" color={colors.text.title}>
            {new Date().getFullYear()}{" "}
            {getCMSDataField(
              cmsData,
              "componentsFooter.copyRightText",
              "The Open Data Company B.V. All Rights Reserved"
            )}
          </Typography>
        </Box>
        {!mobile && (
          <Typography
            fontWeight="700"
            color={colors.text.title}
            sx={{ fontSize: "18px !important" }}
          >
            ·
          </Typography>
        )}
        <Link to="/privacy">
          {getCMSDataField(
            cmsData,
            "componentsFooter.privacyLinkText",
            "Privacy"
          )}
        </Link>
        <Link to="/terms-and-conditions">
          {getCMSDataField(
            cmsData,
            "componentsFooter.termsLinkText",
            "Terms and Conditions"
          )}
        </Link>
        <Link to="/disclaimer">
          {getCMSDataField(
            cmsData,
            "componentsFooter.disclaimerLinkText",
            "Disclaimer"
          )}
        </Link>
      </Box>
    </FooterEl>
  );
};
